'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowRight, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MagneticButton } from '@/components/shared/magnetic-button';
import { TextAccent } from '@/components/shared/text-accent';
import { contactSchema, type ContactInput } from '@/lib/schemas/contact';
import { submitContactForm } from '@/lib/actions/contact';
import { cn } from '@/lib/utils';

const BUDGET_OPTIONS: { value: ContactInput['budget']; label: string }[] = [
  { value: '<25k', label: '< $25k' },
  { value: '25-100k', label: '$25–100k' },
  { value: '100-500k', label: '$100–500k' },
  { value: '500k+', label: '$500k+' },
];

const WHAT_YOU_GET: string[] = [
  'A written architecture for one specific workflow',
  'Two senior engineers — not sales reps',
  '90 focused minutes with a working agenda',
  'An honest answer: we tell you if we can\'t help',
];

export function CTA() {
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ContactInput>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      company: '',
      role: '',
      budget: undefined,
      message: '',
      website: '',
    },
  });

  const selectedBudget = watch('budget');

  const onSubmit = async (data: ContactInput) => {
    const result = await submitContactForm(data);
    if (result.ok) {
      setSubmitted(true);
      toast.success('Message sent. We\'ll be in touch within 1 business day.');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <section
      id="contact"
      aria-labelledby="cta-title"
      className="relative overflow-hidden border-t border-(--color-border) bg-(--color-bg-elevated)"
    >
      {/* Brand glow — signals "act here" to the eye */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px]"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 0%, hsl(202 78% 53% / 0.10), transparent)',
        }}
      />
      <div className="relative mx-auto max-w-(--container-max) px-6 py-24 md:py-32">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr]">
          {/* Left: value proposition */}
          <div>
            {/* Eyebrow */}
            <p className="mb-5 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-(--color-text-subtle)">
              <span aria-hidden="true" className="block h-px w-6 bg-(--color-accent)" />
              Contact
            </p>

            <h2
              id="cta-title"
              className="text-3xl font-semibold tracking-tight text-(--color-text) md:text-4xl"
            >
              Free <TextAccent>AI audit</TextAccent> — no slides, no upsell.
            </h2>

            <p className="mt-5 max-w-[52ch] text-base text-(--color-text-muted) leading-relaxed">
              Two engineers spend 90 minutes with you, walk away with a written architecture
              for one workflow. If we can&rsquo;t help, we&rsquo;ll tell you who can.
            </p>

            {/* What you get */}
            <ul className="mt-8 space-y-3" aria-label="What you get">
              {WHAT_YOU_GET.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span
                    aria-hidden="true"
                    className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-(--color-surface) border border-(--color-border)"
                  >
                    <Check size={11} strokeWidth={2} className="text-(--color-accent)" />
                  </span>
                  <span className="text-sm text-(--color-text-muted)">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: form or success state */}
          <div className="card-premium rounded-md p-7 lg:p-8">
            {submitted ? (
              <SuccessState />
            ) : (
              <form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                className="space-y-4"
                aria-label="Contact form"
              >
                {/* Honeypot — invisible to humans */}
                <input
                  {...register('website')}
                  type="text"
                  name="website"
                  autoComplete="off"
                  tabIndex={-1}
                  aria-hidden="true"
                  className="sr-only"
                />

                {/* Name */}
                <div className="space-y-1.5">
                  <Label htmlFor="cf-name" className="text-sm text-(--color-text-muted)">
                    Full name <span aria-hidden="true" className="text-(--color-accent)">*</span>
                  </Label>
                  <Input
                    id="cf-name"
                    type="text"
                    autoComplete="name"
                    placeholder="Jane Smith"
                    aria-required="true"
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? 'cf-name-err' : undefined}
                    {...register('name')}
                    className={inputCn(!!errors.name)}
                  />
                  {errors.name && (
                    <p id="cf-name-err" role="alert" className={errorCn}>
                      {errors.name.message}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <Label htmlFor="cf-email" className="text-sm text-(--color-text-muted)">
                    Work email <span aria-hidden="true" className="text-(--color-accent)">*</span>
                  </Label>
                  <Input
                    id="cf-email"
                    type="email"
                    autoComplete="email"
                    placeholder="jane@company.com"
                    aria-required="true"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'cf-email-err' : undefined}
                    {...register('email')}
                    className={inputCn(!!errors.email)}
                  />
                  {errors.email && (
                    <p id="cf-email-err" role="alert" className={errorCn}>
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Company */}
                <div className="space-y-1.5">
                  <Label htmlFor="cf-company" className="text-sm text-(--color-text-muted)">
                    Company <span aria-hidden="true" className="text-(--color-accent)">*</span>
                  </Label>
                  <Input
                    id="cf-company"
                    type="text"
                    autoComplete="organization"
                    placeholder="Acme Corp"
                    aria-required="true"
                    aria-invalid={!!errors.company}
                    aria-describedby={errors.company ? 'cf-company-err' : undefined}
                    {...register('company')}
                    className={inputCn(!!errors.company)}
                  />
                  {errors.company && (
                    <p id="cf-company-err" role="alert" className={errorCn}>
                      {errors.company.message}
                    </p>
                  )}
                </div>

                {/* Role (optional) */}
                <div className="space-y-1.5">
                  <Label htmlFor="cf-role" className="text-sm text-(--color-text-muted)">
                    Your role{' '}
                    <span className="text-(--color-text-subtle) font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="cf-role"
                    type="text"
                    autoComplete="organization-title"
                    placeholder="Head of Operations"
                    aria-invalid={!!errors.role}
                    aria-describedby={errors.role ? 'cf-role-err' : undefined}
                    {...register('role')}
                    className={inputCn(!!errors.role)}
                  />
                  {errors.role && (
                    <p id="cf-role-err" role="alert" className={errorCn}>
                      {errors.role.message}
                    </p>
                  )}
                </div>

                {/* Budget — segmented toggle row */}
                <fieldset className="space-y-1.5">
                  <legend className="text-sm text-(--color-text-muted) font-medium leading-none select-none">
                    Budget range <span aria-hidden="true" className="text-(--color-accent)">*</span>
                  </legend>
                  <div
                    role="radiogroup"
                    aria-required="true"
                    aria-label="Budget range"
                    className="mt-1.5 flex flex-wrap gap-2"
                  >
                    {BUDGET_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={selectedBudget === opt.value}
                        onClick={() => setValue('budget', opt.value, { shouldValidate: true })}
                        className={cn(
                          'rounded-md border px-3.5 py-2 text-sm font-medium transition-colors duration-[var(--duration-fast)] ease-[var(--ease-out)]',
                          'focus-visible:outline-2 focus-visible:outline-(--color-accent) focus-visible:outline-offset-2',
                          selectedBudget === opt.value
                            ? 'border-(--color-accent) bg-(--color-surface) text-(--color-accent)'
                            : 'border-(--color-border) bg-(--color-bg-elevated) text-(--color-text-muted) hover:border-(--color-border-hover) hover:text-(--color-text)',
                        )}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {errors.budget && (
                    <p role="alert" className={errorCn}>
                      {errors.budget.message}
                    </p>
                  )}
                </fieldset>

                {/* Message */}
                <div className="space-y-1.5">
                  <Label htmlFor="cf-message" className="text-sm text-(--color-text-muted)">
                    Message <span aria-hidden="true" className="text-(--color-accent)">*</span>
                  </Label>
                  <Textarea
                    id="cf-message"
                    rows={4}
                    placeholder="Tell us what you're trying to automate or where you're losing the most time."
                    aria-required="true"
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? 'cf-message-err' : undefined}
                    {...register('message')}
                    className={cn(inputCn(!!errors.message), 'min-h-[100px] resize-y')}
                  />
                  {errors.message && (
                    <p id="cf-message-err" role="alert" className={errorCn}>
                      {errors.message.message}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <div className="pt-1">
                  <MagneticButton
                    type="submit"
                    disabled={isSubmitting}
                    aria-busy={isSubmitting}
                    className="w-full sm:w-auto"
                  >
                    {isSubmitting ? (
                      'Sending…'
                    ) : (
                      <>
                        Send
                        <ArrowRight
                          size={16}
                          strokeWidth={1.5}
                          aria-hidden="true"
                          className="transition-transform duration-[var(--duration-fast)] ease-[var(--ease-out)] group-hover:translate-x-0.5"
                        />
                      </>
                    )}
                  </MagneticButton>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function SuccessState() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col items-center justify-center rounded-md border border-(--color-border) bg-(--color-surface) py-16 px-8 text-center"
    >
      <span
        aria-hidden="true"
        className="mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-(--color-border) bg-(--color-bg-elevated)"
      >
        <Check size={24} strokeWidth={1.5} className="text-(--color-accent)" />
      </span>
      <h3 className="text-xl font-semibold text-(--color-text) mb-2">You&rsquo;re in the queue.</h3>
      <p className="text-sm text-(--color-text-muted) max-w-[34ch] leading-relaxed">
        Thanks — we&rsquo;ll be in touch within 1 business day.
      </p>
    </div>
  );
}

function inputCn(invalid: boolean) {
  return cn(
    'bg-(--color-bg-elevated) border-(--color-border)',
    'focus-visible:border-(--color-accent) focus-visible:ring-0',
    'placeholder:text-(--color-text-subtle) text-(--color-text)',
    'h-11 px-3.5',
    invalid && 'border-red-500/60',
  );
}

const errorCn =
  'mt-1 text-xs text-red-400 font-mono' as const;
